import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FEEDBACK_FEATURES, RATING_FEATURES } from "@/lib/features";

const PMF_CHOICES = [
  { id: "very_disappointed",     label: "Very disappointed" },
  { id: "somewhat_disappointed", label: "Somewhat disappointed" },
  { id: "not_disappointed",      label: "Not disappointed" },
];

const TEAM_INVITED = [
  { id: "yes",      label: "Yes, they're using it" },
  { id: "not_yet",  label: "Not yet" },
  { id: "planning", label: "Planning to soon" },
];

const TESTIMONIAL = [
  { id: "yes_named",     label: "Yes — quote me by name" },
  { id: "yes_anonymous", label: "Yes — but keep me anonymous" },
  { id: "no",            label: "No thanks" },
];

// Small 1-5 star input
function StarRow({ value, onChange, testId }) {
  return (
    <div className="flex gap-1" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          data-testid={`${testId}-${n}`}
          className="p-1"
          aria-label={`${n} of 5`}
        >
          <Star className={`w-6 h-6 ${value >= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

// Full-modal wrapper
function Shell({ title, subtitle, children, footer, onClose, busy, testId }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={() => !busy && onClose()}
      data-testid={testId}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => e.preventDefault()}
        className="w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-bold">Beta milestone</div>
            <div className="font-display text-xl font-extrabold mt-0.5">{title}</div>
            {subtitle && <div className="text-xs text-slate-500 mt-1">{subtitle}</div>}
          </div>
          <button type="button" onClick={onClose} disabled={busy} className="text-slate-400 hover:text-slate-700" data-testid={`${testId}-close`}>
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
        <div className="flex justify-end gap-2 mt-5">
          {footer}
        </div>
      </form>
    </div>
  );
}

export default function MilestoneModal({ name, onClose }) {
  const { user } = useAuth();
  const isClinic = user?.beta_type === "clinic";
  const [busy, setBusy] = useState(false);

  // Universal state
  const [experienceRating, setExperienceRating] = useState(0);
  const [mostUseful, setMostUseful] = useState("");
  const [friction, setFriction] = useState("");
  const [pmfAnswer, setPmfAnswer] = useState("");
  const [nps, setNps] = useState(null);
  const [ratings, setRatings] = useState({}); // feature_id -> 1..5
  const [topRequest, setTopRequest] = useState("");
  const [mostValuable, setMostValuable] = useState("");
  const [changeOne, setChangeOne] = useState("");
  const [testimonial, setTestimonial] = useState("");
  const [teamInvited, setTeamInvited] = useState("");
  const [teamSize, setTeamSize] = useState("");

  const setRating = (fid, val) => setRatings((prev) => ({ ...prev, [fid]: val }));

  const submit = async () => {
    setBusy(true);
    const payload = { experience_rating: experienceRating || null };
    if (name === "day7_checkin") {
      payload.most_useful_feature = mostUseful || null;
      payload.friction_note = friction.trim();
      if (!experienceRating) { toast.error("Give us a rating first"); setBusy(false); return; }
    }
    if (name === "day30_product") {
      if (!pmfAnswer) { toast.error("Please answer the PMF question"); setBusy(false); return; }
      payload.pmf_answer = pmfAnswer;
      payload.per_feature_ratings = Object.fromEntries(Object.entries(ratings).filter(([, v]) => v));
      payload.top_feature_request = topRequest.trim();
      if (isClinic) payload.team_invited = teamInvited || null;
    }
    if (name === "final_pmf") {
      if (!pmfAnswer) { toast.error("Please answer the PMF question"); setBusy(false); return; }
      if (nps === null || nps === "") { toast.error("Please pick an NPS score"); setBusy(false); return; }
      payload.pmf_answer = pmfAnswer;
      payload.nps = Number(nps);
      payload.most_valuable_feature = mostValuable || null;
      payload.change_one_thing = changeOne.trim();
      payload.testimonial_permission = testimonial || null;
      if (isClinic && teamSize) payload.team_size_using = Number(teamSize);
    }
    try {
      await http.post(`/beta/milestone/${name}`, payload);
      toast.success("Thanks — recorded.");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const submitBtn = (
    <>
      <Button type="button" variant="ghost" onClick={onClose} className="rounded-full" disabled={busy}>Cancel</Button>
      <Button type="button" onClick={submit} disabled={busy} className="rounded-full" data-testid={`milestone-submit-${name}`}>
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Submit"}
      </Button>
    </>
  );

  // ---------- Day 7 ----------
  if (name === "day7_checkin") {
    return (
      <Shell title="How's your first week?" subtitle="30-second check-in" onClose={onClose} busy={busy} testId="milestone-modal-day7_checkin" footer={submitBtn}>
        <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-bold">Overall experience</label>
        <div className="mt-2 mb-4"><StarRow value={experienceRating} onChange={setExperienceRating} testId="milestone-day7-stars" /></div>

        <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-bold">What's been most useful?</label>
        <select
          value={mostUseful}
          onChange={(e) => setMostUseful(e.target.value)}
          data-testid="milestone-day7-mostuseful"
          className="mt-1.5 mb-4 w-full h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="">— Skip —</option>
          {FEEDBACK_FEATURES.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>

        <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-bold">Any friction to report?</label>
        <Textarea
          value={friction}
          onChange={(e) => setFriction(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Optional — what tripped you up?"
          data-testid="milestone-day7-friction"
          className="rounded-xl resize-none mt-1.5"
        />
      </Shell>
    );
  }

  // ---------- Day 30 ----------
  if (name === "day30_product") {
    return (
      <Shell title="You've had a month with Discrimina" subtitle="Help shape what's next" onClose={onClose} busy={busy} testId="milestone-modal-day30_product" footer={submitBtn}>
        <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-bold">How would you feel if you could no longer use Discrimina?</label>
        <div className="grid gap-2 mt-2 mb-4" data-testid="milestone-day30-pmf">
          {PMF_CHOICES.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setPmfAnswer(c.id)}
              data-testid={`milestone-day30-pmf-${c.id}`}
              className={`text-left px-4 py-2.5 rounded-xl border text-sm transition-colors ${
                pmfAnswer === c.id ? "bg-teal-50 border-teal-500 text-teal-900 font-semibold" : "bg-white border-slate-200 hover:border-slate-400"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-bold">Rate the features you've used</label>
        <div className="grid gap-2 mt-2 mb-4">
          {RATING_FEATURES.map((f) => (
            <div key={f.id} className="flex items-center justify-between gap-3 py-1" data-testid={`milestone-day30-rating-row-${f.id}`}>
              <div className="text-sm text-slate-700">{f.label}</div>
              <StarRow value={ratings[f.id] || 0} onChange={(v) => setRating(f.id, v)} testId={`milestone-day30-rating-${f.id}`} />
            </div>
          ))}
        </div>

        <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-bold">What's the ONE thing we should build next?</label>
        <Textarea
          value={topRequest}
          onChange={(e) => setTopRequest(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Optional but incredibly helpful."
          data-testid="milestone-day30-toprequest"
          className="rounded-xl resize-none mt-1.5 mb-4"
        />

        {isClinic && (
          <div data-testid="milestone-day30-clinic-branch">
            <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-bold">Have you invited your team?</label>
            <div className="grid gap-2 mt-2">
              {TEAM_INVITED.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setTeamInvited(c.id)}
                  data-testid={`milestone-day30-teaminvited-${c.id}`}
                  className={`text-left px-4 py-2.5 rounded-xl border text-sm transition-colors ${
                    teamInvited === c.id ? "bg-teal-50 border-teal-500 text-teal-900 font-semibold" : "bg-white border-slate-200 hover:border-slate-400"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </Shell>
    );
  }

  // ---------- Final PMF ----------
  if (name === "final_pmf") {
    return (
      <Shell title="You made it — final survey" subtitle="Two minutes. Then we buy you coffee." onClose={onClose} busy={busy} testId="milestone-modal-final_pmf" footer={submitBtn}>
        <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-bold">How would you feel if you could no longer use Discrimina?</label>
        <div className="grid gap-2 mt-2 mb-4" data-testid="milestone-final-pmf">
          {PMF_CHOICES.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setPmfAnswer(c.id)}
              data-testid={`milestone-final-pmf-${c.id}`}
              className={`text-left px-4 py-2.5 rounded-xl border text-sm transition-colors ${
                pmfAnswer === c.id ? "bg-teal-50 border-teal-500 text-teal-900 font-semibold" : "bg-white border-slate-200 hover:border-slate-400"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-bold">On a scale of 0–10, how likely are you to recommend Discrimina?</label>
        <div className="flex flex-wrap gap-1.5 mt-2 mb-4" data-testid="milestone-final-nps">
          {Array.from({ length: 11 }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setNps(i)}
              data-testid={`milestone-final-nps-${i}`}
              className={`w-9 h-9 rounded-lg text-xs font-semibold border ${
                nps === i ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
              }`}
            >
              {i}
            </button>
          ))}
        </div>

        <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-bold">Most valuable feature</label>
        <select
          value={mostValuable}
          onChange={(e) => setMostValuable(e.target.value)}
          data-testid="milestone-final-mostvaluable"
          className="mt-1.5 mb-4 w-full h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="">— Skip —</option>
          {FEEDBACK_FEATURES.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>

        <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-bold">One thing you'd change</label>
        <Textarea
          value={changeOne}
          onChange={(e) => setChangeOne(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Optional — pick the biggest one."
          data-testid="milestone-final-changeone"
          className="rounded-xl resize-none mt-1.5 mb-4"
        />

        {isClinic && (
          <div className="mb-4" data-testid="milestone-final-clinic-branch">
            <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-bold">How many people on your team use Discrimina?</label>
            <input
              type="number"
              min={0}
              max={999}
              value={teamSize}
              onChange={(e) => setTeamSize(e.target.value)}
              data-testid="milestone-final-teamsize"
              className="mt-1.5 w-full h-10 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="e.g. 3"
            />
          </div>
        )}

        <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-bold">May we quote your feedback in marketing?</label>
        <div className="grid gap-2 mt-2" data-testid="milestone-final-testimonial">
          {TESTIMONIAL.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setTestimonial(c.id)}
              data-testid={`milestone-final-testimonial-${c.id}`}
              className={`text-left px-4 py-2.5 rounded-xl border text-sm transition-colors ${
                testimonial === c.id ? "bg-teal-50 border-teal-500 text-teal-900 font-semibold" : "bg-white border-slate-200 hover:border-slate-400"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </Shell>
    );
  }

  return null;
}
